import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Etape } from '@dinertournant/core';
import { BandeauSignature, Embleme, IconeCommune, IconeEtape, IconeMaison } from '../../components/index.js';
import { api, capitale } from '../../lib/api.js';

export interface EtapeParcours {
  etape: Etape | 'commune';
  label: string;
  heure: string;
  heureCourte: string;
  heureFin: string;
  heureFinCourte: string;
  disponible: boolean;
  jeRecois?: boolean;
  hote?: { nom: string; nomComplet: string; adresse: string | null; complementAdresse: string | null; telephone: string | null };
  adresseVisible?: boolean;
  adresseVisibleA?: string | null;
  adresseVisibleAHeure?: string | null;
  nombreConvives?: number;
  regimesConvives?: string[];
  nombrePersonnes?: number;
  /** Étape commune de fin de soirée : `hote` porte le lieu et son adresse. */
  commune?: boolean;
}
export interface DonneesParcours {
  event: { nom: string; organisation: string | null; date: string; dateLongue: string; zone: string | null; horaires: Record<Etape, string>; contact: { nom: string | null; telephone: string | null }; commune?: { nom: string; heure: string } | null };
  foyer: { id: string; nom: string; membres: string[]; prenoms: string };
  etapeHote: Etape | null;
  etapesHote?: Etape[];
  parcoursDisponible: boolean;
  nombreTables: number;
  parcours: EtapeParcours[];
}

const ARTICLE: Record<Etape | 'commune', string> = { entree: 'une entrée', plat: 'un plat', dessert: 'un dessert', commune: 'la fin de soirée' };
const FIN_DE: Record<Etape | 'commune', string> = { entree: "en fin d'après-midi", plat: "en fin d'entrée", dessert: 'en fin de plat', commune: 'en fin de dessert' };

function texteDuree(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const j = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (j > 0) return `${j} j ${h} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')}`;
  if (m > 0) return `${m} min ${String(s).padStart(2, '0')} s`;
  return `${s} s`;
}

/** Petit compte à rebours jusqu'au dévoilement d'un hôte ; prévient à l'échéance pour recharger le parcours. */
export function CompteARebours({ cible, onEcheance }: { cible: string; onEcheance?: (() => void) | undefined }) {
  const echeance = new Date(cible).getTime();
  const [reste, setReste] = useState(() => echeance - Date.now());
  useEffect(() => {
    setReste(echeance - Date.now());
    const id = window.setInterval(() => setReste(echeance - Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [echeance]);
  const fini = reste <= 0;
  // À l'échéance : on prévient tout de suite, puis toutes les 5 s tant que l'hôte n'est pas apparu (horloges légèrement décalées).
  useEffect(() => {
    if (!fini || !onEcheance) return;
    onEcheance();
    const id = window.setInterval(onEcheance, 5000);
    return () => window.clearInterval(id);
  }, [fini, onEcheance]);
  return (
    <span className="compte-a-rebours" role="timer" aria-live="off" title="Temps restant avant le dévoilement de l'hôte">
      <span aria-hidden="true">⏳</span> {fini ? 'dévoilement…' : `dans ${texteDuree(reste)}`}
    </span>
  );
}

function lienItineraire(adresse: string, zone: string | null): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(zone ? `${adresse}, ${zone}` : adresse)}&travelmode=walking`;
}

/** Écran 7 : parcours de la soirée (page mobile). Composant pur, utilisé par /p/:token et l'aperçu organisateur. */
export function VueParcours({ data, onEcheance }: { data: DonneesParcours; onEcheance?: () => void }) {
  const pluriel = data.foyer.membres.length > 1;
  const vous = pluriel ? 'vous compris' : 'vous compris·e';
  // Compte à rebours sur la prochaine étape encore masquée.
  const prochaineMasquee = data.parcours.find((e) => e.disponible && e.hote && !e.jeRecois && !e.adresseVisible && e.adresseVisibleA)?.etape ?? null;
  return (
    <div className="page-mobile parcours">
      <BandeauSignature />
      <header className="pile" style={{ padding: '24px 20px 8px', gap: 6 }}>
        <Embleme />
        <div className="surtitre">{data.event.nom}</div>
        <h1 className="display" style={{ fontSize: 34 }}>
          Ce soir, {data.foyer.prenoms}
        </h1>
        <p className="texte-doux">
          {capitale(data.event.dateLongue)}
          {data.parcoursDisponible ? ` · 3 étapes, ${data.nombreTables} table${data.nombreTables > 1 ? 's' : ''}` : ''}
        </p>
      </header>

      {!data.parcoursDisponible ? (
        <div style={{ padding: '12px 20px' }}>
          <div className="carte pile">
            <h2 style={{ fontSize: 18 }}>Votre parcours arrive bientôt</h2>
            <p className="texte-doux">L'organisation constitue les tables. Vous recevrez un e-mail dès que votre parcours sera prêt : où aller à chaque étape, et qui vous recevez le cas échéant.</p>
          </div>
        </div>
      ) : (
        <ol className="frise">
          {data.parcours.map((e, i) => (
            <li key={e.etape} className={`etape-${e.etape}`}>
              <div className="rail">
                <span className="rond">{e.etape === 'commune' ? <IconeCommune taille={20} /> : <IconeEtape etape={e.etape} taille={20} />}</span>
                {i < data.parcours.length - 1 ? <span className="trait" /> : null}
              </div>
              <div className="contenu">
                <div className="heure-ligne">
                  <span className="heure">{e.heureCourte}</span>
                  <span className="libelle">{e.label}</span>
                  {e.etape === prochaineMasquee && e.adresseVisibleA ? <CompteARebours cible={e.adresseVisibleA} onEcheance={onEcheance} /> : null}
                </div>
                {!e.disponible || !e.hote ? (
                  <div className="carte-etape">
                    <p className="texte-muet">Pas de table pour cette étape.</p>
                  </div>
                ) : e.commune ? (
                  <div className="carte-etape commune">
                    <div className="texte-muet">Tout le monde se retrouve</div>
                    <div className="display nom-hote">{e.adresseVisible ? e.hote.nomComplet : <span aria-label="lieu surprise">???</span>}</div>
                    {e.adresseVisible && e.hote.adresse ? (
                      <>
                        <div className="adresse-ligne">
                          <span aria-hidden="true">📍</span>
                          <span>
                            {e.hote.adresse}
                            {e.hote.complementAdresse ? `, ${e.hote.complementAdresse}` : ''}
                          </span>
                        </div>
                        <p>Tous les Lions de la soirée, {e.nombrePersonnes} personnes.</p>
                        <a className="btn-itineraire" href={lienItineraire(e.hote.adresse, data.event.zone)} target="_blank" rel="noreferrer">
                          ➤ Itinéraire
                        </a>
                      </>
                    ) : (
                      <>
                        <div className="adresse-cachee">
                          <span aria-hidden="true">🔒</span>
                          <span>
                            <strong>Lieu et adresse dévoilés à {e.adresseVisibleAHeure ?? e.heureCourte}</strong>
                            <br />
                            Gardez la surprise : on vous les révèle en fin de dessert. Revenez sur cette page le moment venu.
                          </span>
                        </div>
                        <p>Tous les Lions de la soirée, {e.nombrePersonnes} personnes.</p>
                      </>
                    )}
                  </div>
                ) : e.jeRecois ? (
                  <div className="carte-etape recevez">
                    <div className="surtitre-clair">
                      <IconeMaison taille={18} /> Vous recevez
                    </div>
                    <div className="titre-clair">Chez vous{e.hote.adresse ? `, ${e.hote.adresse}` : ''}</div>
                    <p>
                      Préparez {ARTICLE[e.etape]} pour <strong>{e.nombrePersonnes} personnes</strong>, {vous}. Vos convives arrivent vers {e.heureCourte} et repartent à {e.heureFinCourte}.
                    </p>
                    <div className="convives">
                      <div className="convives-titre">
                        {e.nombreConvives} convive{(e.nombreConvives ?? 0) > 1 ? 's' : ''} · surprise jusqu'à leur arrivée
                      </div>
                      <div className="convive" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                        <span>Régimes à prévoir</span>
                        {e.regimesConvives && e.regimesConvives.length ? (
                          <span className="ligne" style={{ gap: 6, flexWrap: 'wrap' }}>
                            {e.regimesConvives.map((r, i) => (
                              <span key={i} className="regime">
                                {r}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="tiret">aucun signalé</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="carte-etape invite">
                    <div className="texte-muet">Vous êtes invité{pluriel ? '·e·s' : '·e'} chez</div>
                    <div className="display nom-hote">{e.adresseVisible ? e.hote.nomComplet : <span aria-label="hôte surprise">???</span>}</div>
                    {e.adresseVisible && e.hote.adresse ? (
                      <>
                        <div className="adresse-ligne">
                          <span aria-hidden="true">📍</span>
                          <span>
                            {e.hote.adresse}
                            {e.hote.complementAdresse ? `, ${e.hote.complementAdresse}` : ''}
                            {e.hote.telephone ? (
                              <>
                                <br />
                                <span className="texte-muet">Tél. {e.hote.telephone}</span>
                              </>
                            ) : null}
                          </span>
                        </div>
                        <p>Vous serez {e.nombrePersonnes} à table.</p>
                        <a className="btn-itineraire" href={lienItineraire(e.hote.adresse, data.event.zone)} target="_blank" rel="noreferrer">
                          ➤ Itinéraire
                        </a>
                      </>
                    ) : (
                      <>
                        <div className="adresse-cachee">
                          <span aria-hidden="true">🔒</span>
                          <span>
                            <strong>Hôte et adresse dévoilés à {e.adresseVisibleAHeure ?? e.heureCourte}</strong>
                            <br />
                            Gardez la surprise : on vous les révèle {FIN_DE[e.etape]}. Revenez sur cette page le moment venu.
                          </span>
                        </div>
                        <p>Vous serez {e.nombrePersonnes} à table.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {data.event.contact.nom || data.event.contact.telephone ? (
        <div style={{ padding: '8px 20px 0' }}>
          <div className="carte contact-orga">
            <div>
              <strong>Un imprévu ?</strong>
              <div className="texte-doux">Prévenez l'organisation{data.event.contact.nom ? `, ${data.event.contact.nom}` : ''}</div>
            </div>
            {data.event.contact.telephone ? (
              <a className="btn" href={`tel:${data.event.contact.telephone.replace(/\s+/g, '')}`}>
                Appeler
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Route publique /p/:token */
export function ParcoursPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<DonneesParcours | null>(null);
  const [etat, setEtat] = useState<'chargement' | 'ok' | 'introuvable'>('chargement');
  const [version, setVersion] = useState(0);
  // À l'échéance du compte à rebours, on recharge le parcours : l'hôte apparaît sans toucher à la page.
  const recharger = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    let actif = true;
    api
      .get<DonneesParcours>(`/api/public/p/${token}`)
      .then((d) => {
        if (!actif) return;
        setData(d);
        setEtat('ok');
      })
      .catch(() => actif && setEtat('introuvable'));
    return () => {
      actif = false;
    };
  }, [token, version]);
  if (etat === 'chargement')
    return (
      <div className="page-mobile">
        <BandeauSignature />
        <p className="texte-muet" style={{ padding: 28 }}>
          Chargement…
        </p>
      </div>
    );
  if (etat === 'introuvable' || !data)
    return (
      <div className="page-mobile">
        <BandeauSignature />
        <header style={{ padding: '28px 20px' }} className="pile">
          <h1 className="display">Ce lien ne mène nulle part</h1>
          <p className="texte-doux">Reprenez le lien de votre dernier e-mail : un nouvel envoi remplace le précédent.</p>
        </header>
      </div>
    );
  return <VueParcours data={data} onEcheance={recharger} />;
}
